// Tests for students: the list, a test's page, taking it (autosave, timer) and the marked attempt.
// Teachers and admins get their own screens (tests-staff.jsx) on the same addresses.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Loading, Notice, Section, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { Rich } from '../ui/rich.jsx';
import { AnswerInput, QuestionReview, Scenario, answered, marksLabel, percent, scoreText } from '../ui/question.jsx';
import { dateTime, plural } from '../format.js';
import { MarkScript, StaffTest, StaffTests } from './tests-staff.jsx';

const STATUS = {
  in_progress: ['gold', 'In progress'],
  open: ['gold', 'To do'],
  again: ['ok', 'Done'],
  upcoming: ['plain', 'Coming up'],
  done: ['ok', 'Done'],
  marking: ['navy', 'Being marked'],
  missed: ['bad', 'Missed'],
};

export function testFacts(z) {
  const c = z.counts || {};
  return [
    plural(Number(c.questions || 0), 'question'),
    c.marks ? marksLabel(c.marks) : null,
    z.time_limit_min ? `${z.time_limit_min} min` : 'No time limit',
  ].filter(Boolean).join(' · ');
}

function windowText(z) {
  if (z.opens_at && new Date(z.opens_at) > new Date()) return `Opens ${dateTime(z.opens_at)}`;
  if (z.closes_at) return new Date(z.closes_at) > new Date() ? `Closes ${dateTime(z.closes_at)}` : `Closed ${dateTime(z.closes_at)}`;
  return '';
}

export function TestCard({ z }) {
  const [tone, label] = STATUS[z.status] || STATUS.open;
  const best = z.best_score !== null && z.best_score !== undefined && Number(z.best_of) > 0;
  return (
    <li>
      <Link class={`test-row is-${z.status}`} href={`/tests/${z.id}`}>
        <span class="subject">{(z.batch && z.batch.subject) || '•'}</span>
        <span class="test-row-text">
          <strong>{z.title}{z.kind === 'mock' ? <>{' '}<span class="kind-tag">Mock exam</span></> : null}</strong>
          <small>{[z.batch && z.batch.name, z.class && z.class.title].filter(Boolean).join(' · ')}</small>
          <small>{[testFacts(z), windowText(z)].filter(Boolean).join(' · ')}</small>
        </span>
        <span class="test-row-end">
          <Chip tone={tone}>{label}</Chip>
          {best ? <span class="test-best">{percent(z.best_score, z.best_of)}%</span> : null}
        </span>
      </Link>
    </li>
  );
}

/* ---------------------------------------------------------------- list */
export function Tests() {
  const { me } = useApp();
  if (me.role !== 'student') return <StaffTests />;
  return <StudentTests />;
}

function StudentTests() {
  const { reloadHome } = useApp();
  const { data, error, loading, reload } = useRpc('my_quizzes', {});
  useEffect(() => { reloadHome(true); }, []);
  const groups = useMemo(() => {
    const all = (data && data.quizzes) || [];
    return [
      ['To do', all.filter((z) => ['in_progress', 'open'].includes(z.status))],
      ['Coming up', all.filter((z) => z.status === 'upcoming')],
      ['Done', all.filter((z) => ['done', 'again', 'marking'].includes(z.status))],
      ['Missed', all.filter((z) => z.status === 'missed')],
    ].filter(([, list]) => list.length);
  }, [data]);
  return (
    <Shell title="Tests">
      <p class="muted">Practice tests and mock exams from your teachers. Practice tests show your marks as soon as you submit; mock exams once your teacher has marked them.</p>
      {loading && !data ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.quizzes.length ? <Empty icon="tests" title="No tests yet">When your teacher sets a test, it appears here and on your phone if reminders are on.</Empty> : null}
      {groups.map(([title, list]) => (
        <Section title={title} id={`t-${title.replace(/\W+/g, '-').toLowerCase()}`} key={title}>
          <ul class="list test-list">{list.map((z) => <TestCard z={z} key={z.id} />)}</ul>
        </Section>
      ))}
    </Shell>
  );
}

/* ---------------------------------------------------------------- one test */
export function TestPage({ id }) {
  const { me } = useApp();
  if (me.role !== 'student') return <StaffTest id={id} />;
  return <StudentTest id={id} />;
}

function StudentTest({ id }) {
  const toast = useToast();
  const { data: z, error, loading, reload } = useRpc('get_quiz', { p_quiz_id: id }, [id]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const a = await rpc('start_quiz_attempt', { p_quiz_id: id });
      navigate(`/tests/attempts/${a.id}`);
    } catch (ex) { toast(ex.message, 'bad'); setBusy(false); reload(true); }
  };
  const go = () => (z.time_limit_min && !z.in_progress ? setConfirm(true) : start());
  const canStart = z && ['open', 'again', 'in_progress'].includes(z.status);
  const mock = z && z.kind === 'mock';
  return (
    <Shell backTo="/tests" backLabel="Tests">
      {loading && !z ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {z ? (
        <article class="test-page">
          <p class="eyebrow">{[z.batch.name, z.class && z.class.title].filter(Boolean).join(' · ')}</p>
          <h1 class="page-title">{z.title}</h1>
          {mock ? <p class="kind-line"><span class="kind-tag">Mock exam</span></p> : null}
          <dl class="facts">
            <div><dt>Questions</dt><dd>{z.counts.questions}{z.counts.written ? ` (${z.counts.written} written, ${mock ? 'marked by your teacher' : 'checked by you'})` : ''}</dd></div>
            <div><dt>Marks</dt><dd>{z.counts.marks}</dd></div>
            <div><dt>Time</dt><dd>{z.time_limit_min ? `${z.time_limit_min} minutes` : 'No time limit'}</dd></div>
            <div><dt>Attempts</dt><dd>{mock ? 'One sitting' : z.attempts_allowed ? `${z.attempts_used} of ${z.attempts_allowed} used` : `${z.attempts_used} so far · no limit`}</dd></div>
            {z.opens_at ? <div><dt>Opens</dt><dd>{dateTime(z.opens_at)}</dd></div> : null}
            {z.closes_at ? <div><dt>Closes</dt><dd>{dateTime(z.closes_at)}</dd></div> : null}
          </dl>
          {z.instructions ? <div class="card instructions"><Rich text={z.instructions} /></div> : null}
          {mock && z.status !== 'done' && z.status !== 'marking' ? (
            <Notice icon="info">This is a mock exam: one sitting{z.time_limit_min ? `, ${z.time_limit_min} minutes` : ''}. Write your answers as you would in the exam; for written answers you can add photos of workings done on paper. Your teacher marks it, then you get your marks, their comments and the answers together.</Notice>
          ) : null}
          {!mock && z.reveal === 'after_close' && !z.reveals_now ? <Notice icon="info">You’ll see the correct answers and explanations once the test closes{z.closes_at ? ` on ${dateTime(z.closes_at)}` : ''}.</Notice> : null}
          {z.status === 'upcoming' ? <Notice icon="clock">This test opens on {dateTime(z.opens_at)}.</Notice> : null}
          {z.status === 'missed' ? <Notice tone="warn" icon="alert">This test closed before you took it.</Notice> : null}
          <div class="row">
            {canStart ? (
              <Button variant="gold" icon={z.in_progress ? 'next' : 'tests'} busy={busy} onClick={go}>
                {z.in_progress ? 'Carry on' : z.attempts_used ? 'Try again' : mock ? 'Start the mock' : 'Start the test'}
              </Button>
            ) : null}
          </div>
          {z.attempts.length ? (
            <Section title="Your attempts" id="t-attempts">
              <ul class="list">
                {z.attempts.map((a) => (
                  <li key={a.id}>
                    <Link class="list-row attempt-row" href={`/tests/attempts/${a.id}`}>
                      <span class="attempt-no">{a.attempt_no}</span>
                      <span class="list-text">
                        <strong>{!a.submitted_at ? 'In progress' : a.waiting ? 'Being marked' : `${scoreText(a.score, a.max_score)} · ${percent(a.score, a.max_score)}%`}</strong>
                        <small>{a.submitted_at ? `Submitted ${dateTime(a.submitted_at)}${a.auto_submitted ? ' (time ran out)' : ''}` : `Started ${dateTime(a.started_at)}`}</small>
                      </span>
                      <Icon name="next" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <Sheet title="Ready to start?" open={confirm} onClose={() => setConfirm(false)}>
            <div class="form">
              <p>You have <strong>{z.time_limit_min} minutes</strong>. The clock keeps running if you close the app, and when time is up your saved answers are submitted.</p>
              <div class="form-actions">
                <Button variant="gold" icon="timer" busy={busy} onClick={start}>Start the clock</Button>
                <Button variant="ghost" onClick={() => setConfirm(false)}>Not yet</Button>
              </div>
            </div>
          </Sheet>
        </article>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- an attempt */
export function Attempt({ id }) {
  const { data, error, loading, reload, setData } = useRpc('get_quiz_attempt', { p_attempt_id: id }, [id]);
  const { me, reloadHome } = useApp();
  if (loading && !data) return <Shell backTo="/tests" backLabel="Tests"><Loading rows={4} /></Shell>;
  if (error) return <Shell backTo="/tests" backLabel="Tests"><ErrorBox error={error} onRetry={reload} /></Shell>;
  if (!data) return null;
  if (!data.submitted_at && data.student && data.student.id === (me && me.id)) {
    return <TakeTest attempt={data} onSubmitted={(a) => { setData(a); reloadHome(true); window.scrollTo(0, 0); }} onGone={() => reload()} />;
  }
  if (data.submitted_at) saveLocal(data.id, null); // nothing left to send for this attempt
  if (me.role !== 'student' && data.quiz.kind === 'mock' && data.submitted_at) return <MarkScript a={data} onSaved={setData} />;
  return <Review a={data} staff={me.role !== 'student'} />;
}

// Answers not yet on the server are kept on the phone too, so a dropped connection or a closed tab loses
// nothing: { v: 2, pending: { <item id>: { r: answer, t: when it was given, in server time } }, flags, at }.
const LOCAL_PREFIX = 'vv-attempt-';
const LOCAL = (id) => `${LOCAL_PREFIX}${id}`;
function loadLocal(id) {
  try { return JSON.parse(localStorage.getItem(LOCAL(id)) || 'null'); } catch (e) { return null; }
}
function saveLocal(id, v) {
  try { if (v) localStorage.setItem(LOCAL(id), JSON.stringify(v)); else localStorage.removeItem(LOCAL(id)); } catch (e) { /* private mode */ }
}

// On signing out, so the next person on a shared phone can't read them.
export function forgetLocalAttempts() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LOCAL_PREFIX)) localStorage.removeItem(k);
    }
  } catch (e) { /* private mode */ }
}

// The kept answers that are still worth sending: those given after the server's copy was saved (it may
// have come from another device since).
function keptAnswers(attempt) {
  const l = loadLocal(attempt.id);
  if (!l || l.v !== 2) return { pending: {}, pendingAt: {}, flags: [], at: 0 };
  const savedAt = new Map(attempt.items.map((it) => [it.id, it.saved_at ? new Date(it.saved_at).getTime() : 0]));
  const pending = {};
  const pendingAt = {};
  for (const [k, v] of Object.entries(l.pending || {})) {
    if (savedAt.has(k) && v && typeof v.t === 'number' && v.t > savedAt.get(k)) { pending[k] = v.r === undefined ? null : v.r; pendingAt[k] = v.t; }
  }
  return { pending, pendingAt, flags: (Array.isArray(l.flags) ? l.flags : []).filter((f) => savedAt.has(f)), at: Number(l.at) || 0 };
}

function clock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

function TakeTest({ attempt, onSubmitted, onGone }) {
  const toast = useToast();
  const items = attempt.items;
  const local = useMemo(() => keptAnswers(attempt), [attempt.id]);
  const mock = attempt.quiz.kind === 'mock';
  const [answers, setAnswers] = useState(() => {
    const out = {};
    for (const it of items) {
      out[it.id] = it.q.type === 'written' && it.photos && it.photos.length ? { ...(it.response || {}), photos: it.photos } : (it.response || null);
    }
    return { ...out, ...local.pending };
  });
  const latest = useRef(answers); // every answer as last changed, ahead of the next render
  const [uploading, setUploading] = useState({}); // photos on their way, by question
  const uploadingRef = useRef({});
  const onUploading = (itemId, delta) => {
    const next = { ...uploadingRef.current, [itemId]: Math.max(0, (uploadingRef.current[itemId] || 0) + delta) };
    uploadingRef.current = next;
    setUploading(next);
  };
  const uploadsLeft = () => Object.values(uploadingRef.current).reduce((t, n) => t + n, 0);
  const [flags, setFlags] = useState(() => new Set(local.flags));
  const [at, setAt] = useState(() => Math.max(0, Math.min(local.at, items.length - 1)));
  const [save, setSave] = useState('saved'); // saved | saving | waiting | offline | error
  const [saveError, setSaveError] = useState('');
  const [gridOpen, setGridOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pending = useRef({ ...local.pending });
  const pendingAt = useRef({ ...local.pendingAt });
  const timer = useRef(null);
  const inFlight = useRef(null);
  const alive = useRef(true);
  const gone = useRef(false);
  const flagsRef = useRef(flags);
  const atRef = useRef(at);
  flagsRef.current = flags;
  atRef.current = at;
  const offset = useMemo(() => new Date(attempt.server_now).getTime() - Date.now(), [attempt.id]);
  const deadline = attempt.deadline_at ? new Date(attempt.deadline_at).getTime() : null;
  const [now, setNow] = useState(() => Date.now() + offset);
  const done = useRef(false);
  const hasPending = () => Object.keys(pending.current).length > 0;

  const persist = (f = flagsRef.current, i = atRef.current) => {
    const kept = {};
    for (const [k, r] of Object.entries(pending.current)) kept[k] = { r, t: pendingAt.current[k] || 0 };
    saveLocal(attempt.id, { v: 2, pending: kept, flags: [...f], at: i });
  };

  // One save at a time, so an older answer can never arrive after a newer one.
  const saveOnce = async () => {
    const batch = { ...pending.current };
    if (!Object.keys(batch).length) { setSave('saved'); return true; }
    setSave('saving');
    try {
      await rpc('save_quiz_answers', { p_attempt_id: attempt.id, p_answers: batch });
      for (const k of Object.keys(batch)) {
        if (pending.current[k] === batch[k]) { delete pending.current[k]; delete pendingAt.current[k]; }
      }
      persist();
      setSave(hasPending() ? 'waiting' : 'saved');
      return true;
    } catch (ex) {
      if (ex.reason === 'submitted' || ex.reason === 'not_found') {
        // Submitted already (time ran out), or the test was taken down: show where things stand. Answers
        // kept on the phone stay for a test that comes back.
        gone.current = true;
        if (ex.reason === 'submitted') saveLocal(attempt.id, null);
        if (alive.current) onGone();
        return false;
      }
      setSave(ex.reason === 'offline' ? 'offline' : 'error');
      setSaveError(ex.message);
      if (alive.current) timer.current = setTimeout(flush, ex.reason === 'offline' ? 5000 : 15000);
      return false;
    }
  };
  const flush = async () => {
    clearTimeout(timer.current);
    while (inFlight.current) await inFlight.current;
    if (gone.current) return false;
    inFlight.current = saveOnce();
    const ok = await inFlight.current;
    inFlight.current = null;
    if (ok && hasPending()) return flush();
    return ok;
  };

  const change = (itemId, next) => {
    const r = typeof next === 'function' ? next(latest.current[itemId]) : next;
    latest.current = { ...latest.current, [itemId]: r };
    setAnswers(latest.current);
    pending.current = { ...pending.current, [itemId]: r };
    pendingAt.current = { ...pendingAt.current, [itemId]: Date.now() + offset };
    persist();
    setSave('waiting');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  };

  // Submits only once every answer is on the server. When time runs out the server still takes answers
  // for 30 seconds, so a dropped connection gets until then to come back.
  const submit = async (auto) => {
    if (done.current) return;
    if (!auto && uploadsLeft()) {
      setConfirm(false);
      toast('A photo is still uploading. Submit once it has finished.', 'bad');
      return;
    }
    done.current = true;
    setSubmitting(true);
    // Out of time: photos still uploading get the server's 30 seconds of grace too.
    while (auto && uploadsLeft() && alive.current && Date.now() + offset < deadline + 20000) {
      await new Promise((r) => { setTimeout(r, 500); });
    }
    let saved = await flush();
    while (auto && !saved && !gone.current && alive.current && Date.now() + offset < deadline + 25000) {
      await new Promise((r) => { setTimeout(r, 3000); });
      saved = await flush();
    }
    if (gone.current || !alive.current) return;
    if (!saved && !auto) {
      done.current = false;
      setSubmitting(false);
      setConfirm(false);
      toast('Your latest answers aren’t saved yet, so the test wasn’t submitted. Check your connection, then try again.', 'bad');
      return;
    }
    try {
      const a = await rpc('submit_quiz_attempt', { p_attempt_id: attempt.id });
      saveLocal(attempt.id, null);
      if (!auto) toast(mock ? 'Submitted. Your teacher will mark it.' : 'Submitted', 'ok', { replace: true });
      else if (saved) toast('Time is up. Your answers were submitted.', 'ok', { replace: true });
      else toast('Time is up. The answers saved before your connection dropped were submitted.', 'bad', { replace: true });
      onSubmitted(a);
    } catch (ex) {
      setSubmitting(false);
      toast(ex.message, 'bad');
      // Out of time: the server submits it with the saved answers; show that once the app reaches it.
      if (auto) onGone(); else done.current = false;
    }
  };

  useEffect(() => {
    if (!deadline) return undefined;
    const t = setInterval(() => setNow(Date.now() + offset), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (deadline && now >= deadline && !done.current) submit(true); }, [now]);
  useEffect(() => {
    const warn = (e) => { if (hasPending()) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    if (hasPending()) flush();
    return () => {
      window.removeEventListener('beforeunload', warn);
      clearTimeout(timer.current);
      // Leaving the test ("Leave for now", the back button): send what's waiting rather than wait 900 ms.
      if (hasPending() && !gone.current && !done.current) flush();
      alive.current = false;
    };
  }, []);

  const go = (i) => { setAt(i); persist(flags, i); setGridOpen(false); window.scrollTo(0, 0); };
  const toggleFlag = (itemId) => {
    const next = new Set(flags);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    setFlags(next);
    persist(next);
  };
  const item = items[at];
  const left = deadline ? deadline - now : null;
  const count = items.filter((it) => answered(it.q, answers[it.id])).length;
  const saveText = {
    saved: 'All answers saved', saving: 'Saving…', waiting: 'Saving…',
    offline: 'Not saved yet. Check your connection; it keeps trying.',
    error: `Not saved yet: ${saveError} It keeps trying.`,
  }[save];

  return (
    <Shell backTo={`/tests/${attempt.quiz.id}`} backLabel="Leave for now"
      actions={left !== null ? <span class={`timer${left < 5 * 60000 ? ' is-low' : ''}`} role="timer" aria-label={`Time left ${clock(left)}`}><Icon name="timer" />{clock(left)}</span> : null}>
      <div class="take">
        <div class="take-head">
          <p class="eyebrow">{attempt.quiz.title}</p>
          <p class={`save-state is-${save}`} role="status">{saveText}</p>
        </div>
        <section class="q-card" aria-labelledby="q-title">
          <header class="q-card-head">
            <h1 id="q-title">Question {at + 1} <span>of {items.length}</span></h1>
            <span class="q-card-meta">{marksLabel(item.q.marks)}</span>
            <button type="button" class={`flag-btn${flags.has(item.id) ? ' is-on' : ''}`} aria-pressed={flags.has(item.id) ? 'true' : 'false'} onClick={() => toggleFlag(item.id)}>
              <Icon name="flag" /><span>{flags.has(item.id) ? 'Flagged' : 'Flag'}</span>
            </button>
          </header>
          <Scenario q={item.q} />
          <Rich text={item.q.body} class="q-body" />
          {item.q.type === 'written' ? (
            <Notice icon="info">{mock
              ? 'Your teacher marks this answer. Type it as you would in the exam; you can add photos of workings done on paper.'
              : 'Practice question: after you submit, compare your answer with the model answer.'}</Notice>
          ) : null}
          <AnswerInput key={item.id} q={item.q} value={answers[item.id]} idBase={`q${at}`} onChange={(r) => change(item.id, r)} allowPhotos={mock}
            uploading={uploading[item.id] || 0} onUploading={(d) => onUploading(item.id, d)} />
        </section>
        <nav class="take-nav" aria-label="Questions">
          <Button variant="line" icon="prev" disabled={at === 0} onClick={() => go(at - 1)} aria-label="Previous question"><span class="nav-label">Previous</span></Button>
          <Button variant="ghost" icon="grid" onClick={() => setGridOpen(true)}>{count}/{items.length} answered</Button>
          {at < items.length - 1
            ? <Button variant="navy" iconRight="next" onClick={() => go(at + 1)}>Next</Button>
            : <Button variant="gold" icon="check" onClick={() => setConfirm(true)}>Finish</Button>}
        </nav>
      </div>
      <Sheet title="All questions" open={gridOpen} onClose={() => setGridOpen(false)}>
        <ol class="q-grid">
          {items.map((it, i) => {
            const state = answered(it.q, answers[it.id]) ? 'answered' : 'empty';
            return (
              <li key={it.id}>
                <button type="button" class={`q-dot is-${state}${flags.has(it.id) ? ' is-flagged' : ''}${i === at ? ' is-current' : ''}`}
                  aria-label={`Question ${i + 1}, ${state === 'answered' ? 'answered' : 'not answered'}${flags.has(it.id) ? ', flagged' : ''}`}
                  aria-current={i === at ? 'step' : undefined} onClick={() => go(i)}>
                  {i + 1}{flags.has(it.id) ? <Icon name="flag" /> : null}
                </button>
              </li>
            );
          })}
        </ol>
        <p class="q-grid-key"><span class="q-dot is-answered" aria-hidden="true">1</span> answered <span class="q-dot is-empty" aria-hidden="true">2</span> not answered <span class="q-dot is-flagged" aria-hidden="true">3<Icon name="flag" /></span> flagged</p>
        <div class="form-actions"><Button variant="gold" icon="check" onClick={() => { setGridOpen(false); setConfirm(true); }}>Finish and submit</Button></div>
      </Sheet>
      <Sheet title="Submit your answers?" open={confirm} onClose={() => setConfirm(false)}>
        <div class="form">
          <p>You have answered <strong>{count} of {items.length}</strong> questions{flags.size ? `, and flagged ${flags.size}` : ''}.{count < items.length ? ' Unanswered questions get no marks.' : ''}</p>
          <div class="form-actions">
            <Button variant="gold" icon="check" busy={submitting} onClick={() => submit(false)}>Submit</Button>
            <Button variant="ghost" onClick={() => setConfirm(false)}>Keep going</Button>
          </div>
        </div>
      </Sheet>
    </Shell>
  );
}

function Review({ a, staff }) {
  const { data: z } = useRpc('get_quiz', { p_quiz_id: a.quiz.id }, [a.quiz.id], { skip: staff });
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const pct = percent(a.score, a.max_score);
  const again = !staff && z && ['open', 'again'].includes(z.status);
  const tryAgain = async () => {
    setBusy(true);
    try { const n = await rpc('start_quiz_attempt', { p_quiz_id: a.quiz.id }); navigate(`/tests/attempts/${n.id}`); } catch (ex) { toast(ex.message, 'bad'); setBusy(false); }
  };
  const written = a.items.filter((it) => it.q.type === 'written').length;
  const mock = a.quiz.kind === 'mock';
  const waiting = mock && a.submitted_at && (a.score === null || a.score === undefined);
  return (
    <Shell backTo={staff ? `/tests/${a.quiz.id}/results` : `/tests/${a.quiz.id}`} backLabel={staff ? 'Results' : 'Test'}>
      <article class="review">
        <p class="eyebrow">{a.quiz.batch ? a.quiz.batch.name : ''}{staff && a.student ? ` · ${a.student.name}` : ''}</p>
        <h1 class="page-title">{a.quiz.title}</h1>
        {waiting ? (
          <div class="score-card is-waiting">
            <div class="score-ring is-waiting" aria-hidden="true"><Icon name="check" /></div>
            <div class="score-text">
              <strong>Submitted</strong>
              <span>{dateTime(a.submitted_at)}{a.auto_submitted ? ' · time ran out, so the saved answers went in' : ''}</span>
              <span>Your teacher marks the written answers. You’ll get a notification when your marks are ready; then you’ll see them here with their comments and the answers.</span>
            </div>
          </div>
        ) : a.submitted_at ? (
          <div class="score-card">
            <div class={`score-ring${pct >= 50 ? ' is-pass' : ''}`} role="img" aria-label={`${pct} percent`} style={{ '--p': pct }}><span>{pct}%</span></div>
            <div class="score-text">
              <strong>{scoreText(a.score, a.max_score)} marks</strong>
              <span>Attempt {a.attempt_no} · submitted {dateTime(a.submitted_at)}</span>
              {a.auto_submitted ? <span>Time ran out, so the saved answers were submitted.</span> : null}
              {written && !mock ? <span>{plural(written, 'written question')} to check against the model answer (not in the score).</span> : null}
            </div>
          </div>
        ) : <Notice icon="clock">{staff ? 'The student is still taking this test.' : 'Still in progress.'}</Notice>}
        {a.feedback ? (
          <div class="feedback-card">
            <h2>Your teacher’s feedback</h2>
            <Rich text={a.feedback} />
          </div>
        ) : null}
        {a.submitted_at && !a.revealed && !mock ? <Notice icon="info">The correct answers and explanations appear when the test closes{a.quiz.closes_at ? ` on ${dateTime(a.quiz.closes_at)}` : ''}.</Notice> : null}
        {again ? <div class="row"><Button variant="gold" icon="refresh" busy={busy} onClick={tryAgain}>Try again</Button></div> : null}
        <div class="review-list">
          {a.items.map((it) => <QuestionReview item={it} revealed={a.revealed} number={it.number} key={it.id} mock={mock} />)}
        </div>
      </article>
    </Shell>
  );
}
